// Third-Party
import React from 'react';
import ReactDOM from 'react-dom';
import debounce from 'lodash/debounce';

// Components
import CommentContent from './CommentContent';
import Line from './Line';
import ComparisonItem from '@/types/comparison-item';
import ComparisonCategory from '@/types/comparison-category';
import ComparisonField from '@/types/comparison-field';
import Coordinate from '@/types/coordinate';

type Props = {
  id: string;
  dotNumber?: number;
  comment?: string;
  isEditing: boolean;
  isPosted?: boolean;
  timestamp?: string;
  userInfos?: {
    imgUrl?: string;
    name?: string;
  };
  onChange?: (event: React.FormEvent<HTMLTextAreaElement>) => void;
  editComment?: () => void;
  deleteComment?: () => void;
  actionsDisabled?: boolean;
  item: ComparisonItem;
  category: ComparisonCategory;
  field: ComparisonField;
  updateTableHeight: (height: number) => void;
  resizeCommentLinesContainer: () => void;
  viewMarkup: boolean;
};

type State = {
  yPos: number;
  lineCoordinates?: Coordinate[];
  calcYPosTimeout?: number;
};

class CommentExpanded extends React.Component<Props, State> {
  readonly state: State = {
    yPos: 0,
  };

  handleResize = debounce(() => {
    this.setState({ yPos: this.calculateYPos() });
    this.props.resizeCommentLinesContainer();
    this.setLineCoordinates();
  }, 500);

  componentWillMount() {
    this.setState({ yPos: this.calculateDefaultYPos() });
    this.props.resizeCommentLinesContainer();
  }

  componentDidMount() {
    const { id } = this.props;
    window.addEventListener('resize', this.handleResize);

    const calcYPosTimeout = window.setTimeout(
      () =>
        this.setState({ yPos: this.calculateYPos() }, () => {
          const comment = document.getElementById(`comment-${id}`);

          if (comment && this.state.yPos) {
            this.props.updateTableHeight(this.state.yPos + comment.offsetHeight + 16);
          }
          this.setLineCoordinates();
        }),
      300,
    );

    this.setState({ calcYPosTimeout });
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { viewMarkup } = this.props;
    const { calcYPosTimeout } = this.state;

    if (prevState.calcYPosTimeout !== calcYPosTimeout) {
      // Ensures coordinates are set after any transitions (ex. transition when cancelling a combined portfolio)
      setTimeout(() => this.setLineCoordinates(), 600);
    }

    if (prevProps.viewMarkup !== viewMarkup) {
      this.handleResize();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.state.calcYPosTimeout);
  }

  calculateDefaultYPos = () => {
    const { id } = this.props;
    const offsetFromOrigin = 50;

    const markupContainer = document.querySelector(`#cell-${id} .markup-container`) as HTMLElement;

    if (!markupContainer) {
      return 0;
    }

    return markupContainer.offsetTop - offsetFromOrigin;
  };

  calculateYPos = () => {
    const { id, item } = this.props;
    const { yPos } = this.state;
    const commentSpacing = 16;

    if (!yPos) {
      return 0;
    }

    const commentIds = Array.from(document.getElementsByClassName('comment-expanded'))
      .map(c => {
        if (c.id.includes(item._id)) {
          return c.id;
        }

        return null;
      })
      .filter(c => !!c);

    const commentIndex = commentIds.indexOf(`comment-${id}`);
    const commentAboveId = commentIds.slice(commentIndex - 1, commentIndex)[0];
    const commentAbove = commentAboveId && document.getElementById(commentAboveId);

    if (!commentAbove) {
      return yPos;
    }

    const offset = commentAbove.offsetTop + commentAbove.offsetHeight + commentSpacing;
    return offset > yPos ? offset : yPos;
  };

  setLineCoordinates = () => {
    const { id } = this.props;

    const firstCard = document.getElementsByClassName('comparison-card')[0] as HTMLElement;
    const cardsContainer = document.getElementsByClassName('comparison-cards')[0];

    const comment = document.getElementById(`comment-${id}`);
    const cell = document.getElementById(`cell-${id}`);
    const markupContainer = document.querySelector(`#cell-${id} .markup-container`);

    if (!cell || !comment || !markupContainer) {
      this.setState({ lineCoordinates: undefined });
      return;
    }

    // Get coordinates to calculate line
    const { top: markupContainerTop, right: markupContainerRight, height: markupContainerHeight } = markupContainer.getBoundingClientRect();
    const { top: cardsContainerTop } = cardsContainer.getBoundingClientRect();
    const { right: cellRight } = cell.getBoundingClientRect();
    const { left: firstCardLeft } = firstCard.getBoundingClientRect();
    const { left: commentLeft } = comment.getBoundingClientRect();

    // Calculations for the 1st point (Right side of markup container)
    const p1x = markupContainerRight - firstCardLeft;
    const p1y = markupContainerTop - cardsContainerTop + markupContainerHeight / 2;

    // Calculations for the 2st point (Right side of cell)
    const p2x = cellRight - firstCardLeft - 24;
    const p2y = markupContainerTop - cardsContainerTop + markupContainerHeight / 2;

    // Calculations for the 3rd point (Comment number)
    const p3x = commentLeft - firstCardLeft + 1;
    const p3y = comment.offsetTop + firstCard.offsetTop + 4; // 4px extra offset

    const lineP1 = { x: p1x, y: p1y } as Coordinate;
    const lineP2 = { x: p2x, y: p2y } as Coordinate;
    const lineP3 = { x: p3x, y: p3y } as Coordinate;

    this.setState({ lineCoordinates: [lineP1, lineP2, lineP3] });
  };

  renderLine = () => {
    const { lineCoordinates } = this.state;

    if (!lineCoordinates) {
      return <></>;
    }

    return <Line coordinates={lineCoordinates} />;
  };

  render() {
    const { dotNumber, id } = this.props;
    const { yPos } = this.state;
    const linesDOM = document.getElementsByClassName('comment-lines')[0];

    return (
      <div className="comment-expanded" id={`comment-${id}`} onClick={e => e.stopPropagation()} style={{ top: yPos }}>
        <div className="comment-number">{dotNumber}</div>

        <div className="rounded-corner" />

        <CommentContent {...this.props} />

        {linesDOM && ReactDOM.createPortal(this.renderLine(), linesDOM)}
      </div>
    );
  }
}

export default CommentExpanded;
